import { type FormEvent, type KeyboardEvent, useRef } from 'react';
import { cn } from '../../lib/cn.ts';
import { Textarea } from '../textarea/textarea.tsx';
import { ChatComposerAttachButton } from './chat-composer-attach-button.tsx';
import { ChatComposerAttachmentList } from './chat-composer-attachment-list.tsx';
import { ChatComposerDictate } from './chat-composer-dictate.tsx';
import { ChatComposerRecording } from './chat-composer-recording.tsx';
import { ChatComposerSend } from './chat-composer-send.tsx';
import type {
  ChatComposerAttachment,
  ChatComposerDictation,
  ChatComposerDraft,
  ChatComposerLabels,
} from './chat-composer-types.ts';
import { composerLabels } from './composerLabels.ts';
import { useComposerFiles } from './useComposerFiles.ts';
import { useComposerFocus } from './useComposerFocus.ts';
import { useComposerText } from './useComposerText.ts';
import { useEscapeCancelsRecording } from './useEscapeCancelsRecording.ts';
import { useRattle } from './useRattle.ts';

export type {
  ChatComposerAttachment,
  ChatComposerDictation,
  ChatComposerLabels,
  ChatComposerMicrophone,
} from './chat-composer-types.ts';

export interface ChatComposerProps {
  placeholder: string;
  sendLabel: string;
  /** Called with the trimmed text. The field clears itself once it returns. */
  onSend: (text: string) => void;
  disabled?: boolean;
  /**
   * Writing is refused for as long as this stands, not held for a turn the way
   * `disabled` is. Passing it makes the composer something to read, not to
   * use — so give a reason beside it.
   */
  locked?: boolean;
  /**
   * Takes the caret whenever this value changes — the caller passes whatever
   * marks a moment someone is about to write: the chat opening, a conversation
   * being started or picked up.
   */
  focusOn?: string;
  /**
   * Words put in the field by a page, unsent: the caret lands after them, so
   * Enter sends them as they are and typing changes them first. Taken whenever
   * `id` changes — the same words offered twice are offered twice.
   */
  draft?: ChatComposerDraft;
  /**
   * Bumped to rattle the field once: somebody was sent here from elsewhere on
   * the page and has to be shown where "here" is.
   */
  attention?: number;
  /**
   * Files picked for the next message. Passing this — together with `onAttach`
   * — is what puts a paperclip on the composer; a chat without it has no
   * upload at all.
   */
  attachments?: ChatComposerAttachment[];
  /** Files were picked, pasted or dropped. */
  onAttach?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  /** What the file picker offers, as an `accept` list. */
  accept?: string;
  /** Whether more files may still be added — the caller holds the cap. */
  canAttach?: boolean;
  /**
   * Passing this is what puts a microphone on the composer; a chat without it
   * is typed and nothing else.
   */
  dictation?: ChatComposerDictation;
  labels?: ChatComposerLabels;
  className?: string;
}

/**
 * How tall the field may grow before it starts scrolling instead. About five
 * lines: long enough to hold a paragraph whole, short enough that the
 * conversation above it is still the larger half of the panel.
 */
const MAX_FIELD_HEIGHT = 'max-h-30';

/**
 * Where a message is written; how it behaves is in docs/design/app-chat.md.
 * Disabled, it holds the turn but the field stays writable and keeps the caret:
 * a disabled field drops focus, and on a phone that shuts the keyboard on every send.
 */
export function ChatComposer({
  placeholder,
  sendLabel,
  onSend,
  disabled = false,
  locked = false,
  focusOn,
  draft,
  attention,
  attachments = [],
  onAttach,
  onRemoveAttachment,
  accept,
  canAttach = true,
  dictation,
  labels,
  className,
}: ChatComposerProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  useComposerFocus(field, focusOn, disabled);
  const [text, setText] = useComposerText(draft, field);
  const { rattling, onAnimationEnd } = useRattle(attention);
  const copy = composerLabels(labels, sendLabel);
  /* Held for a turn or refused outright: either way nothing goes out. */
  const blocked = disabled || locked;
  const accepting = canAttach && !blocked;
  const files = useComposerFiles(onAttach, accepting);

  /*
   * The dictation while it is running, or null. Held as the object rather than
   * as a flag so that everything the recording row draws is reached through
   * the one thing that proves there is a recording.
   */
  const listening =
    dictation !== undefined && dictation.state !== 'idle' ? dictation : null;
  useEscapeCancelsRecording(listening);

  /* A file still going up would be left behind by a send, text or not. */
  const waiting = attachments.some((attachment) => attachment.uploading);
  const canSend =
    (text.trim().length > 0 || attachments.length > 0) && !waiting && !blocked;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSend) {
      return;
    }

    onSend(text.trim());
    setText('');
    /* Sending with the button moves the caret to it; put it back. */
    field.current?.focus();
  }

  /*
   * A textarea takes Enter as a line rather than as a send, so the submit is
   * asked for explicitly and the keystroke consumed. Shift+Enter writes the
   * line, and Enter that confirms an IME word (Japanese, Chinese) is no send.
   */
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  }

  return (
    <>
      <ChatComposerAttachmentList
        attachments={attachments}
        onRemove={onRemoveAttachment}
        removeLabel={copy.removeAttachment}
      />

      <form
        data-slot='chat-composer'
        onSubmit={submit}
        onDrop={files.onDrop}
        onDragOver={files.onDragOver}
        onDragLeave={files.onDragLeave}
        onAnimationEnd={onAnimationEnd}
        className={cn(
          'flex items-center gap-2 rounded-full border border-line bg-fill-faint py-1.5 pr-1.5',
          rattling && 'motion-safe:animate-attention',
          /* No paperclip while recording, so the row starts where text does. */
          files.uploads && listening === null ? 'pl-1.5' : 'pl-4',
          files.dropping && 'border-ink bg-fill',
          className,
        )}
      >
        {listening !== null ? (
          <ChatComposerRecording listening={listening} cancelLabel={copy.dictateCancel} />
        ) : (
          <>
            {files.uploads ? (
              <ChatComposerAttachButton
                label={copy.attach}
                accept={accept}
                disabled={!accepting}
                onPick={files.take}
              />
            ) : null}

            <Textarea
              ref={field}
              variant='bare'
              rows={1}
              value={text}
              disabled={locked}
              placeholder={placeholder}
              aria-label={placeholder}
              /*
               * A message, not a form field: nothing to fill in from saved
               * passwords or addresses. The return key says "send" because
               * Enter sends.
               */
              autoComplete='off'
              enterKeyHint='send'
              autoCapitalize='sentences'
              className={cn(MAX_FIELD_HEIGHT, 'overflow-y-auto')}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
              onPaste={files.onPaste}
            />

            {dictation !== undefined ? (
              <ChatComposerDictate
                dictation={dictation}
                disabled={blocked}
                startLabel={copy.dictate}
                optionsLabel={copy.dictateOptions}
                microphoneLabel={copy.dictateMicrophone}
              />
            ) : null}
          </>
        )}

        <ChatComposerSend
          listening={listening}
          canSend={canSend}
          sendLabel={sendLabel}
          stopLabel={copy.dictateStop}
          sendingLabel={copy.dictateSending}
        />
      </form>
    </>
  );
}
